import { CONNECTION_CONFIG } from '@Web/config/ConnectionConfig';

export function computeReconnectBackoffMs(
  attempt: number,
  cfg = CONNECTION_CONFIG.reconnect
): number {
  const cappedAttempt = Math.max(1, attempt);
  return Math.min(
    cfg.initialDelayMs * Math.pow(cfg.backoffMultiplier, cappedAttempt - 1),
    cfg.maxDelayMs
  );
}

export function applyReconnectJitter(
  backoffMs: number,
  jitterMs = CONNECTION_CONFIG.reconnect.jitterMs,
  random: () => number = Math.random
): number {
  return Math.round(backoffMs + random() * jitterMs);
}

export function recordPongReceived(): {
  missedPongs: number;
  unhealthy: boolean;
} {
  return { missedPongs: 0, unhealthy: false };
}

export function recordPongMiss(
  missedPongs: number,
  maxMissedPongs = CONNECTION_CONFIG.heartbeat.maxMissedPongs
): { missedPongs: number; unhealthy: boolean } {
  const next = missedPongs + 1;
  return {
    missedPongs: next,
    unhealthy: next >= maxMissedPongs,
  };
}

export function isTickWatchdogStale(input: {
  enabled?: boolean;
  realtimeReady: boolean;
  hidden: boolean;
  pauseWhenHidden?: boolean;
  phase: string;
  lastRealtimeEventAt: number;
  now: number;
  staleTimeoutMs?: number;
}): boolean {
  const enabled = input.enabled ?? CONNECTION_CONFIG.gameWatchdog.enabled;
  const pauseWhenHidden =
    input.pauseWhenHidden ??
    CONNECTION_CONFIG.lifecycle.pauseWatchdogsWhenHidden;
  const staleTimeoutMs =
    input.staleTimeoutMs ?? CONNECTION_CONFIG.gameWatchdog.tickStaleTimeoutMs;

  if (!enabled || !input.realtimeReady) return false;
  if (pauseWhenHidden && input.hidden) return false;
  if (input.phase !== 'FLYING' && input.phase !== 'TAKEOFF') return false;
  if (input.lastRealtimeEventAt <= 0) return false;
  return input.now - input.lastRealtimeEventAt > staleTimeoutMs;
}

export type VisibilityResumeAction =
  | 'ignore'
  | 'reconnect'
  | 'rejoin'
  | 'probe';

export function resolveVisibilityResumeAction(input: {
  documentHidden: boolean;
  connectionStatus: string;
  socketConnected: boolean;
  realtimeReady: boolean;
}): VisibilityResumeAction {
  if (input.documentHidden) return 'ignore';
  if (input.connectionStatus === 'failed') return 'ignore';
  if (!input.socketConnected) return 'reconnect';
  if (input.connectionStatus !== 'connected' || !input.realtimeReady) {
    return 'rejoin';
  }
  return 'probe';
}

export type JoinHandshakeAction =
  | 'done'
  | 'wait_for_socket'
  | 'retry_join'
  | 'reconnect';

export function resolveJoinHandshakeAction(input: {
  connectionStatus: string;
  socketConnected: boolean;
  joinRetryCount: number;
  maxRetries?: number;
}): JoinHandshakeAction {
  if (input.connectionStatus === 'connected') return 'done';
  if (!input.socketConnected) return 'wait_for_socket';
  const maxRetries = input.maxRetries ?? CONNECTION_CONFIG.join.maxRetries;
  if (input.joinRetryCount < maxRetries) return 'retry_join';
  return 'reconnect';
}

export function shouldDropStaleGeneration(
  activeGeneration: number,
  messageGeneration: number
): boolean {
  return (
    activeGeneration > 0 &&
    messageGeneration > 0 &&
    messageGeneration !== activeGeneration
  );
}

export function shouldAcceptMessageBeforeJoin(input: {
  isPong?: boolean;
  isJoinAck?: boolean;
  isError?: boolean;
  isChat?: boolean;
}): boolean {
  return Boolean(
    input.isPong || input.isJoinAck || input.isError || input.isChat
  );
}

export function shouldDispatchReconnect(input: {
  waitingForNetwork: boolean;
  reconnectAlreadyInProgress: boolean;
}): boolean {
  return !input.waitingForNetwork && !input.reconnectAlreadyInProgress;
}

export function shouldFailBootstrapTimeout(connectionStatus: string): boolean {
  return connectionStatus !== 'connected' && connectionStatus !== 'failed';
}

export function shouldAdvanceLocalGameClock(connectionStatus: string): boolean {
  return connectionStatus === 'connected';
}

export function nextReconnectAttemptCount(
  currentAttempts: number,
  maxAttempts = CONNECTION_CONFIG.reconnect.maxAttempts
): number {
  if (currentAttempts < maxAttempts) return currentAttempts + 1;
  return currentAttempts;
}

/** After the attempt cap, keep retrying at max delay — do not emit `failed`. */
export function shouldEmitFailedAfterMaxReconnectAttempts(): boolean {
  return false;
}
